import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import LinesTextarea from '@/components/view/atoms/textarea/LinesTextarea'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { useAuth } from '../../AuthContext'
import ModalOverlay from './ModalOverlay'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TEXTAREA = 'w-full resize-y rounded-lg border border-border-input bg-surface px-2.5 py-1.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const LABEL = 'text-secondary text-[11px] font-medium'
const FIELD = 'flex flex-col gap-1'

// AIプロファイル管理（メニュー⑧）
export default function AIPurchaseAssistAiProfiles() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()

  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedId, setSelectedId] = useState(null)

  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ai-profiles`, { headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setProfiles(d.items || [])
      // 自動選択: 既に選択済みでなければ先頭を選ぶ
      if ((d.items || []).length > 0 && !selectedId) {
        setSelectedId(d.items[0].id)
      }
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [siteId])

  const createNew = async () => {
    const name = window.prompt('新しいプロファイル名を入力してください')
    if (!name || !name.trim()) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/ai-profiles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ name: name.trim() }),
    })
    if (r.ok) {
      const created = await r.json()
      await fetchAll()
      setSelectedId(created.id)
    } else {
      const d = await r.json().catch(() => ({}))
      alert(`作成失敗: ${d.detail || r.status}`)
    }
  }

  const duplicate = async (id) => {
    if (!window.confirm('このプロファイルを複製しますか？')) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/ai-profiles/${id}/duplicate`, {
      method: 'POST', headers: authHeaders(),
    })
    if (r.ok) {
      const created = await r.json()
      await fetchAll()
      setSelectedId(created.id)
    } else {
      const d = await r.json().catch(() => ({}))
      alert(`複製失敗: ${d.detail || r.status}`)
    }
  }

  const remove = async (id, name, inUse) => {
    if (inUse > 0) {
      alert(`このプロファイルは ${inUse} 件の配信で利用中のため削除できません。先に配信側で別プロファイルへ切替してください。`)
      return
    }
    if (!window.confirm(`プロファイル「${name}」を削除します。よろしいですか？\n（バージョン履歴も含めて完全削除されます）`)) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/ai-profiles/${id}`, {
      method: 'DELETE', headers: authHeaders(),
    })
    if (r.ok) {
      await fetchAll()
      if (selectedId === id) setSelectedId(null)
    } else {
      const d = await r.json().catch(() => ({}))
      alert(`削除失敗: ${d.detail || r.status}`)
    }
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='🎭 AIプロファイル管理'
        description='AIの応答振る舞いを制御するプロンプト設定。プロファイル単位で管理し、配信から参照されます。'
      />

      {error && (
        <div className='bg-surface-critical text-critical mb-3 rounded-lg p-2.5 text-xs'>
          取得エラー: {error}
        </div>
      )}

      <div className='grid grid-cols-[300px_1fr] gap-4'>
        {/* 左ペイン: プロファイル一覧 */}
        <div className={`${CARD} p-3`}>
          <div className='mb-2.5 flex items-center justify-between'>
            <h3 className='text-primary m-0 text-xs font-bold'>
              プロファイル一覧
              <span className='text-tertiary ml-1.5 text-[10px] font-normal'>
                {profiles.length} 件
              </span>
            </h3>
            <Button color={ButtonColor.PRIMARY} onClick={createNew}>+ 新規</Button>
          </div>

          {loading ? (
            <div className='text-tertiary py-5 text-center text-[11px]'>読み込み中...</div>
          ) : profiles.length === 0 ? (
            <div className='text-tertiary py-5 text-center text-[11px]'>
              プロファイルがまだありません。<br />「+ 新規」で作成してください。
            </div>
          ) : (
            <div className='flex flex-col gap-1.5'>
              {profiles.map(p => {
                const on = selectedId === p.id
                return (
                  <div
                    key={p.id}
                    onClick={() => setSelectedId(p.id)}
                    className={`cursor-pointer rounded-lg border p-2.5 ${on ? 'bg-surface-emphasis border-emphasis' : 'bg-surface-secondary border-secondary hover:bg-surface-hover'}`}
                  >
                    <div className='mb-1 flex flex-wrap items-center gap-1.5'>
                      <span className='text-tertiary font-mono text-[9px]'>#{p.id}</span>
                      <span className='bg-surface-emphasis text-emphasis rounded px-1.5 text-[9px] font-bold'>v{p.version_number}</span>
                      {!p.is_active && <span className='bg-surface-secondary text-secondary rounded px-1.5 text-[9px] font-bold'>無効</span>}
                    </div>
                    <div className='text-primary mb-1 text-xs font-semibold'>{p.name}</div>
                    <div className='text-secondary text-[10px]'>
                      利用中: {p.in_use_campaign_count || 0} 配信
                    </div>
                    <div className='mt-1.5 flex gap-1'>
                      <Button color={ButtonColor.SECONDARY} onClick={(e) => { e.stopPropagation(); duplicate(p.id) }}>複製</Button>
                      <Button color={ButtonColor.CRITICAL} onClick={(e) => { e.stopPropagation(); remove(p.id, p.name, p.in_use_campaign_count || 0) }}>削除</Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* 右ペイン: エディタ */}
        <div>
          {selectedId ? (
            <ProfileEditor key={selectedId} siteId={siteId} profileId={selectedId} authHeaders={authHeaders} onChanged={fetchAll} />
          ) : (
            <div className={`${CARD} text-tertiary p-10 text-center text-xs`}>
              左ペインからプロファイルを選択してください
            </div>
          )}
        </div>
      </div>
    </Main>
  )
}


// =====================
// エディタ本体
// =====================
function ProfileEditor({ siteId, profileId, authHeaders, onChanged }) {
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState('basic')
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState(null)
  const [showVersions, setShowVersions] = useState(false)

  useEffect(() => {
    setLoading(true)
    fetch(`/api/sites/${siteId}/purchase-assist/ai-profiles/${profileId}`, { headers: authHeaders() })
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(d => { setProfile(d); setDraft(d); setError(null) })
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false))
  }, [siteId, profileId])

  const isDirty = draft && profile && JSON.stringify(draft) !== JSON.stringify(profile)

  const save = async () => {
    if (!draft) return
    const note = window.prompt('変更メモ（任意）— バージョン履歴に残ります', '')
    setSaving(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ai-profiles/${profileId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          name: draft.name,
          description: draft.description,
          tone_config: draft.tone_config,
          response_config: draft.response_config,
          recommend_config: draft.recommend_config,
          system_prompt: draft.system_prompt,
          is_active: draft.is_active,
          change_note: note || null,
        }),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || `HTTP ${r.status}`)
      }
      const updated = await r.json()
      setProfile(updated)
      setDraft(updated)
      if (onChanged) onChanged()
    } catch (e) {
      alert(`保存失敗: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  const reset = () => {
    if (!profile) return
    if (!window.confirm('未保存の変更を破棄します。よろしいですか？')) return
    setDraft(profile)
  }

  if (loading) return <div className={`${CARD} text-tertiary p-8 text-center`}>読み込み中...</div>
  if (error) return <div className={`${CARD} bg-surface-critical text-critical p-3.5`}>取得エラー: {error}</div>
  if (!profile || !draft) return null

  const TABS = [
    { key: 'basic', label: '🎭 基本' },
    { key: 'response', label: '💬 応答' },
    { key: 'recommend', label: '🛒 推薦' },
    { key: 'advanced', label: '⚙️ 上級者' },
    { key: 'preview', label: '🧪 プレビュー' },
  ]

  return (
    <div className={`${CARD} p-4`}>
      {/* ヘッダー */}
      <div className='mb-3 flex flex-wrap items-center justify-between gap-2.5'>
        <div className='flex-1'>
          <div className='flex items-center gap-2'>
            <span className='text-tertiary font-mono text-[10px]'>#{profile.id}</span>
            <span className='bg-surface-emphasis text-emphasis rounded px-1.5 py-px text-[10px] font-bold'>v{profile.version_number}</span>
            <span className='text-tertiary text-[10px]'>更新: {profile.updated_at ? new Date(profile.updated_at).toLocaleString('ja-JP') : '-'}</span>
          </div>
          <input
            value={draft.name}
            onChange={e => setDraft({ ...draft, name: e.target.value })}
            className='text-primary mt-1 w-full rounded-lg border border-border-input bg-surface px-2 py-1 text-base font-bold outline-none hover:border-border-input-hover focus:border-border-input-active'
          />
        </div>
        <div className='flex gap-1.5'>
          <Button color={ButtonColor.SECONDARY} onClick={() => setShowVersions(!showVersions)}>📜 履歴</Button>
          {isDirty && (
            <Button color={ButtonColor.SECONDARY} onClick={reset}>取消</Button>
          )}
          <Button color={ButtonColor.PRIMARY} size='large' disabled={!isDirty || saving} onClick={save}>
            {saving ? '保存中...' : '✓ 保存'}
          </Button>
        </div>
      </div>

      <textarea
        value={draft.description || ''}
        onChange={e => setDraft({ ...draft, description: e.target.value })}
        placeholder='説明（任意）'
        rows={2}
        className={`${TEXTAREA} mb-3`}
      />

      {/* タブ */}
      <div className='mb-3.5 flex flex-wrap gap-1 border-b border-secondary'>
        {TABS.map(t => {
          const on = tab === t.key
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`cursor-pointer border-b-2 px-3.5 py-2 text-xs font-semibold ${on ? 'bg-surface-emphasis text-emphasis border-emphasis' : 'text-secondary hover:bg-surface-hover border-transparent'}`}
            >{t.label}</button>
          )
        })}
      </div>

      {/* タブコンテンツ */}
      {tab === 'basic' && <BasicTab draft={draft} setDraft={setDraft} />}
      {tab === 'response' && <ResponseTab draft={draft} setDraft={setDraft} />}
      {tab === 'recommend' && <RecommendTab draft={draft} setDraft={setDraft} />}
      {tab === 'advanced' && <AdvancedTab draft={draft} setDraft={setDraft} />}
      {tab === 'preview' && <PreviewTab siteId={siteId} profileId={profileId} draft={draft} authHeaders={authHeaders} />}

      {/* バージョン履歴オーバーレイ */}
      {showVersions && (
        <VersionHistoryOverlay
          siteId={siteId}
          profileId={profileId}
          authHeaders={authHeaders}
          onClose={() => setShowVersions(false)}
          onRestored={() => { setShowVersions(false); window.location.reload() }}
        />
      )}
    </div>
  )
}


// =====================
// 基本タブ
// =====================
function BasicTab({ draft, setDraft }) {
  const tone = draft.tone_config || {}
  const update = (k, v) => setDraft({ ...draft, tone_config: { ...tone, [k]: v } })
  return (
    <div className='flex flex-col gap-3.5'>
      <Field label='口調スタイル'>
        <select value={tone.speech_style || 'polite_desumasu'} onChange={e => update('speech_style', e.target.value)} className={`${INPUT} w-full`}>
          <option value='polite_desumasu'>ですます調（標準）</option>
          <option value='formal'>ビジネス丁寧調</option>
          <option value='casual'>カジュアル（くだけた口調）</option>
        </select>
      </Field>
      <Field label='ペルソナ・キャラクター' hint='例: 丁寧で親しみやすいショッピングアシスタント'>
        <input value={tone.character || ''} onChange={e => update('character', e.target.value)} className={`${INPUT} w-full`} placeholder='キャラクターの説明' />
      </Field>
      <Field label='自称' hint='AIが自分を指す呼び方'>
        <input value={tone.self_reference || ''} onChange={e => update('self_reference', e.target.value)} className={`${INPUT} w-full`} placeholder='例: 私 / 当アシスタント / アシスタント' />
      </Field>
      <Field label='フォーマリティ'>
        <select value={tone.formality_level || 'medium'} onChange={e => update('formality_level', e.target.value)} className={`${INPUT} w-full`}>
          <option value='high'>高（ビジネス対応）</option>
          <option value='medium'>中（標準）</option>
          <option value='low'>低（カジュアル）</option>
        </select>
      </Field>
      <Field label='絵文字使用'>
        <select value={tone.emoji_usage || 'sometimes'} onChange={e => update('emoji_usage', e.target.value)} className={`${INPUT} w-full`}>
          <option value='never'>使わない</option>
          <option value='sometimes'>たまに使う（標準）</option>
          <option value='often'>積極的に使う</option>
        </select>
      </Field>

      <div className='mt-2 rounded-lg border border-secondary bg-surface-secondary p-3'>
        <div className='text-emphasis mb-2.5 text-xs font-bold'>📐 応答スタイル詳細（スライダー）</div>
        <SliderField
          label='応答の長さ'
          value={tone.response_length || 0}
          onChange={v => update('response_length', v)}
          leftLabel='短め'
          rightLabel='長め'
        />
        <SliderField
          label='詳細度'
          value={tone.detail_level || 0}
          onChange={v => update('detail_level', v)}
          leftLabel='あっさり'
          rightLabel='詳しく'
        />
        <SliderField
          label='質問返しの頻度'
          value={tone.question_back_frequency || 0}
          onChange={v => update('question_back_frequency', v)}
          leftLabel='控えめ'
          rightLabel='積極的'
        />
      </div>
    </div>
  )
}

function SliderField({ label, value, onChange, leftLabel, rightLabel }) {
  const v = typeof value === 'number' ? value : parseInt(value || 0)
  return (
    <div className='mb-2.5'>
      <div className='text-secondary mb-1 flex justify-between text-[11px]'>
        <span>{label}</span>
        <span className='text-emphasis font-mono'>{v > 0 ? '+' : ''}{v}</span>
      </div>
      <input
        type='range' min={-50} max={50} step={5}
        value={v}
        onChange={e => onChange(parseInt(e.target.value))}
        className='w-full accent-[var(--color-fill-brand)]'
      />
      <div className='text-tertiary mt-px flex justify-between text-[9px]'>
        <span>{leftLabel}</span>
        <span className='text-secondary'>標準</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  )
}


// =====================
// 応答タブ
// =====================
function ResponseTab({ draft, setDraft }) {
  const r = draft.response_config || {}
  const update = (k, v) => setDraft({ ...draft, response_config: { ...r, [k]: v } })
  const sq = Array.isArray(r.suggested_questions) ? r.suggested_questions : []
  return (
    <div className='flex flex-col gap-3.5'>
      {/* 会話の始まり方（配信設定のデザインタブから移設） */}
      <div className='rounded-lg border border-secondary bg-surface-secondary p-3'>
        <div className='text-emphasis mb-1 text-xs font-bold'>👋 会話の始まり方</div>
        <div className='text-secondary mb-2.5 text-[11px]'>
          このプロファイルを参照する<strong>すべての配信</strong>に適用されます。
          配信ごとに変えたい場合はプロファイルを分けてください。
        </div>
        <div className='flex flex-col gap-3.5'>
          <Field
            label='開始メッセージ（最初に固定表示）'
            hint='AIを呼ばずにそのまま表示される1通目。空なら既定の文言が使われます'
          >
            <textarea
              value={r.initial_message || ''}
              onChange={e => update('initial_message', e.target.value)}
              rows={2}
              placeholder='こんにちは！何かお探しでしょうか？'
              className={TEXTAREA}
            />
          </Field>
          <Field
            label='質問例（ユーザーが選ぶ・最大5件）'
            hint='開始メッセージの下にチップで並びます。押すとその文がユーザーの発話として送信されます'
          >
            <LinesTextarea
              rows={5}
              lines={sq}
              max={5}
              onChange={v => update('suggested_questions', v)}
              placeholder='1行に1つずつ'
              className={TEXTAREA}
            />
          </Field>
        </div>
      </div>

      {/* 2026-10-03 追加：応答の言語（services/pa_language.py）。未設定は「お客様の言語に合わせる」 */}
      <Field
        label='応答の言語'
        hint='「お客様の言語に合わせる」では、日本語以外の質問にはその言語で応答し、上の開始メッセージ・質問例やナレッジの固定回答も翻訳して表示します。商品名・価格は日本語の表記のまま、応答の文章で訳を補います'
      >
        <select
          value={r.response_language || 'auto'}
          onChange={e => update('response_language', e.target.value)}
          className={INPUT}
        >
          <option value='auto'>お客様の言語に合わせる（既定）</option>
          <option value='ja'>常に日本語で応答する</option>
        </select>
      </Field>

      <Field
        label='挨拶の方針（AIの応答文に反映）'
        hint='上の「開始メッセージ」とは別。AIが応答を組み立てる際の挨拶の指針としてプロンプトに渡されます'
      >
        <textarea value={r.greeting || ''} onChange={e => update('greeting', e.target.value)} rows={2} className={TEXTAREA} />
      </Field>
      <Field label='エラー時の応答' hint='意図不明・処理失敗時のフォールバック'>
        <textarea value={r.error_response || ''} onChange={e => update('error_response', e.target.value)} rows={2} className={TEXTAREA} />
      </Field>
      <Field label='該当なし時の応答' hint='検索結果が0件のとき'>
        <textarea value={r.no_match_response || ''} onChange={e => update('no_match_response', e.target.value)} rows={2} className={TEXTAREA} />
      </Field>
      <Field label='在庫切れ時の応答'>
        <textarea value={r.out_of_stock || ''} onChange={e => update('out_of_stock', e.target.value)} rows={2} className={TEXTAREA} />
      </Field>
      <Field label='別れの挨拶'>
        <textarea value={r.farewell || ''} onChange={e => update('farewell', e.target.value)} rows={2} className={TEXTAREA} />
      </Field>
    </div>
  )
}


// =====================
// 推薦タブ
// =====================
function RecommendTab({ draft, setDraft }) {
  const r = draft.recommend_config || {}
  const update = (k, v) => setDraft({ ...draft, recommend_config: { ...r, [k]: v } })
  const ngs = Array.isArray(r.ng_phrases) ? r.ng_phrases : []
  const emp = Array.isArray(r.value_emphasis) ? r.value_emphasis : []
  const VALUE_AXES = ['価格', '品質', 'トレンド', '独自性', 'レビュー評価']
  const toggleEmp = (axis) => {
    const next = emp.includes(axis) ? emp.filter(a => a !== axis) : [...emp, axis]
    update('value_emphasis', next)
  }
  return (
    <div className='flex flex-col gap-3.5'>
      <Field label='推薦理由の文体'>
        <select value={r.reason_style || 'concise_friendly'} onChange={e => update('reason_style', e.target.value)} className={`${INPUT} w-full`}>
          <option value='concise_friendly'>簡潔・親しみやすい</option>
          <option value='detailed'>詳細（スペック説明込み）</option>
          <option value='minimal'>ミニマル（商品名のみ）</option>
        </select>
      </Field>
      <Field label='最大推薦件数' hint='1回の応答で提示する商品の上限'>
        <input type='number' min={1} max={10} value={r.max_recommendations || 3} onChange={e => update('max_recommendations', parseInt(e.target.value) || 3)} className={`${INPUT} w-full`} />
      </Field>
      <Field label='価格比較を含める'>
        <label className='text-primary flex items-center gap-1.5 text-xs'>
          <input type='checkbox' checked={!!r.include_price_comparison} onChange={e => update('include_price_comparison', e.target.checked)} className='accent-[var(--color-fill-brand)]' />
          応答に価格を併記する
        </label>
      </Field>

      <div className='rounded-lg border border-secondary bg-surface-secondary p-3'>
        <div className='text-emphasis mb-2.5 text-xs font-bold'>📐 推薦の積極性（スライダー）</div>
        <SliderField
          label='推薦の積極性'
          value={r.recommend_aggressiveness || 0}
          onChange={v => update('recommend_aggressiveness', v)}
          leftLabel='控えめ（求められた時のみ）'
          rightLabel='積極的（適切な機会に提案）'
        />
      </div>

      <Field label='強調したい価値軸（複数選択可）' hint='推薦時にAIが強調するポイントを選択'>
        <div className='flex flex-wrap gap-1.5'>
          {VALUE_AXES.map(axis => {
            const on = emp.includes(axis)
            return (
              <button
                key={axis}
                type='button'
                onClick={() => toggleEmp(axis)}
                className={`cursor-pointer rounded-full border px-3 py-1 text-[11px] font-bold ${on ? 'bg-surface-emphasis text-emphasis border-emphasis' : 'bg-surface-secondary text-secondary border-secondary hover:bg-surface-hover'}`}
              >
                {on ? '✓ ' : ''}{axis}
              </button>
            )
          })}
        </div>
      </Field>

      <Field label='使用禁止フレーズ' hint='「最安」「絶対」「100%」等の断定・誇張表現の制御'>
        <LinesTextarea
          lines={ngs}
          onChange={v => update('ng_phrases', v)}
          rows={4}
          placeholder='1行に1フレーズ'
          className={TEXTAREA}
        />
      </Field>

      {/* ---------------- 在庫・販売状態 ---------------- */}
      <div className='rounded-lg border border-secondary bg-surface-secondary p-3'>
        <div className='text-emphasis mb-1 text-xs font-bold'>📦 在庫・販売状態</div>
        <div className='text-secondary mb-2.5 text-[11px]'>
          在庫切れや販売終了の商品を推薦してしまうと、そのまま顧客からの申し出につながります。
          既定では推薦候補から除外します。
        </div>
        <div className='flex flex-col gap-2.5'>
          <CheckBoxRow
            label='在庫切れの商品を推薦しない'
            checked={r.exclude_out_of_stock !== false}
            onChange={v => update('exclude_out_of_stock', v)}
          />
          <CheckBoxRow
            label='販売終了・販売期間外の商品を推薦しない'
            checked={r.exclude_unsellable !== false}
            onChange={v => update('exclude_unsellable', v)}
          />
          <Field label='在庫切れ時の扱い' hint='「明示して提示」は入荷通知などへ誘導したい場合に使う'>
            <select
              value={r.out_of_stock_behavior || 'exclude'}
              onChange={e => update('out_of_stock_behavior', e.target.value)}
              className={`${INPUT} w-full`}
            >
              <option value='exclude'>候補から除外する（既定）</option>
              <option value='show_with_notice'>在庫切れと明示して提示する</option>
            </select>
          </Field>
          <Field label='「残りわずか」とみなす在庫数' hint='0 で無効。在庫に言及する設定のときだけ使われる'>
            <input
              type='number' min={0} max={999}
              value={r.low_stock_threshold ?? 3}
              onChange={e => update('low_stock_threshold', parseInt(e.target.value) || 0)}
              className={`${INPUT} w-full`}
            />
          </Field>
          <CheckBoxRow
            label='応答で在庫状況に言及する'
            checked={!!r.mention_stock_status}
            onChange={v => update('mention_stock_status', v)}
          />
          {r.mention_stock_status && (
            <div className='bg-surface-warning text-warning rounded-lg p-2.5 text-[11px]'>
              ⚠ 在庫数はマスタ同期の間隔に依存します。「残りわずか」と伝えた直後に売り切れる可能性が
              あるため、しきい値は保守的に設定してください。
            </div>
          )}
        </div>
      </div>

      {/* ---------------- 販促情報の考慮 ---------------- */}
      <div className='rounded-lg border border-secondary bg-surface-secondary p-3'>
        <div className='text-emphasis mb-1 text-xs font-bold'>🎁 販促情報の考慮</div>
        <div className='text-secondary mb-2.5 text-[11px]'>
          クーポンやノベルティは適用条件（対象商品・最低購入金額・併用可否・会員ランク）が複雑です。
          <strong>適用可否はシステム側で判定し、AIには判定済みの内容だけを渡します</strong>が、
          誤案内はクレームに直結するため<strong>既定はすべてOFF</strong>です。必要なものだけ有効にしてください。
        </div>
        <div className='flex flex-col gap-2.5'>
          <CheckBoxRow
            label='セール価格を現在価格として使う'
            checked={r.use_sale_price !== false}
            onChange={v => update('use_sale_price', v)}
          />
          <CheckBoxRow label='クーポンを案内する' checked={!!r.mention_coupons} onChange={v => update('mention_coupons', v)} />
          {r.mention_coupons && (
            <Field label='案内するクーポンの範囲'>
              <select
                value={r.coupon_scope || 'public_only'}
                onChange={e => update('coupon_scope', e.target.value)}
                className={`${INPUT} w-full`}
              >
                <option value='public_only'>公開クーポンのみ（既定）</option>
                <option value='include_member'>ログイン時は保有クーポンも案内</option>
              </select>
            </Field>
          )}
          <CheckBoxRow label='セット割・まとめ買いを案内する' checked={!!r.mention_set_promotions} onChange={v => update('mention_set_promotions', v)} />
          <CheckBoxRow label='ノベルティ・特典を案内する' checked={!!r.mention_novelty} onChange={v => update('mention_novelty', v)} />
          <CheckBoxRow label='ポイントキャンペーンを案内する' checked={!!r.mention_points} onChange={v => update('mention_points', v)} />
          <Field label='販促の伝え方'>
            <select
              value={r.promo_expression || 'factual'}
              onChange={e => update('promo_expression', e.target.value)}
              className={`${INPUT} w-full`}
            >
              <option value='factual'>事実として淡々と伝える（既定）</option>
              <option value='urgency'>期限・残数を添えて訴求する</option>
            </select>
          </Field>
          <CheckBoxRow
            label='会員ランク限定の販促はランクが確認できるときだけ案内する'
            checked={r.respect_member_rank !== false}
            onChange={v => update('respect_member_rank', v)}
          />
          <Field label='ランクが確認できない場合' hint='未ログインや顧客IDが渡らない設置での挙動'>
            <select
              value={r.member_rank_unknown_behavior || 'hide'}
              onChange={e => update('member_rank_unknown_behavior', e.target.value)}
              className={`${INPUT} w-full`}
            >
              <option value='hide'>ランク限定の販促は案内しない（既定）</option>
              <option value='show_as_conditional'>「会員ランクにより異なる」と条件付きで触れる</option>
            </select>
          </Field>
          <div className='text-tertiary text-[10px]'>
            会員ランクは、設置サイトから渡された顧客IDで顧客マスタを照会して判定します
            （申告されたランクは使いません）。
          </div>
        </div>
      </div>
    </div>
  )
}

/** チェックボックス1行（推薦タブ内で使う軽量な行） */
function CheckBoxRow({ label, checked, onChange }) {
  return (
    <label className='text-primary flex items-center gap-1.5 text-xs'>
      <input
        type='checkbox'
        checked={!!checked}
        onChange={e => onChange(e.target.checked)}
        className='accent-[var(--color-fill-brand)]'
      />
      {label}
    </label>
  )
}


// =====================
// 上級者タブ（system_prompt 直接編集）
// =====================
function AdvancedTab({ draft, setDraft }) {
  return (
    <div>
      <Field label='システムプロンプト（直接編集）' hint='基本/応答/推薦の設定値はこのプロンプトの後ろに自動で合成されます'>
        <textarea
          value={draft.system_prompt || ''}
          onChange={e => setDraft({ ...draft, system_prompt: e.target.value })}
          rows={20}
          className={`${TEXTAREA} font-mono text-[11px]`}
        />
      </Field>
      <div className='bg-surface-warning text-secondary mt-2 rounded-lg border border-secondary p-2.5 text-[11px]'>
        ⚠️ 上級者向け: 直接編集はLLM動作に大きな影響を与えます。変更後は必ず「プレビュー」タブで動作確認してください。
      </div>
    </div>
  )
}


// =====================
// プレビュータブ
// =====================
// 応答の出どころ（NGで止めた・ナレッジで答えた 等）の表示名
function answerSourceLabel(meta) {
  switch (meta?.answer_source) {
    case 'ng_blocked': return '🚫 NGワード・禁止トピックで停止'
    case 'knowledge': return `📚 ナレッジ${meta.knowledge_title ? `「${meta.knowledge_title}」` : ''}`
    case 'preset': return '📌 プリセット'
    case 'escalated': return '🙋 有人対応へ引き継ぎ'
    case 'prompt_only': return '🤖 AI（プロンプトのみ）'
    default: return '🤖 AI'
  }
}
function PreviewTab({ siteId, profileId, draft, authHeaders }) {
  const [userMessage, setUserMessage] = useState('')
  const [history, setHistory] = useState([]) // [{role, content}]
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  // '' / 'light' / 'standard'。2026-10-06 修正（SMART_HUB_DEV-159）：旧 'haiku' / 'sonnet'。
  // 中身はシステム設定＞AIモデルで選んだ Findee の軽量/標準モデル
  const [model, setModel] = useState('')
  // 2026-10-03 追加（SMART_HUB_DEV-98）：公開中のウィジェットと同じ処理（NG・ナレッジ・商品検索）で試す
  const [fullPipeline, setFullPipeline] = useState(true)
  const [sessionToken, setSessionToken] = useState(null) // 本番と同じ処理での会話の続き

  const send = async () => {
    if (!userMessage.trim()) return
    setLoading(true)
    setResult(null)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ai-profiles/${profileId}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          user_message: userMessage,
          // 表示用に持たせた推薦商品は送らない（サーバは role / content だけを受け取る）
          history: history.map(({ role, content }) => ({ role, content })),
          // 未保存の draft も試せるように設定を上書き
          tone_config: draft.tone_config,
          response_config: draft.response_config,
          recommend_config: draft.recommend_config,
          system_prompt: draft.system_prompt,
          model: fullPipeline ? null : (model || null),
          full_pipeline: fullPipeline,
          session_token: fullPipeline ? sessionToken : null,
        }),
      })
      const d = await r.json()
      setResult(d)
      if (d.ok) {
        if (d.session_token) setSessionToken(d.session_token)
        setHistory([...history, { role: 'user', content: userMessage }, { role: 'assistant', content: d.response, products: d.recommendations || [] }])
        setUserMessage('')
      }
    } catch (e) {
      setResult({ ok: false, error: e.message })
    } finally {
      setLoading(false)
    }
  }

  const reset = () => { setHistory([]); setResult(null); setSessionToken(null) }

  const changeMode = (full) => {
    // 処理を切り替えたら会話を最初からにする（両者で会話の続き方が違うため）
    setFullPipeline(full)
    reset()
  }

  return (
    <div className='flex flex-col gap-3'>
      <div className='bg-surface-info text-secondary rounded-lg border border-secondary p-2.5 text-[11px]'>
        💡 編集中の設定（未保存も含む）でAI応答を試せます。
        {/* 2026-10-03 修正：NGワードが効くのか分からないという指摘（SMART_HUB_DEV-98） */}
        <br />
        {fullPipeline ? (
          <>
            公開中のウィジェットと同じく、<strong>NGワード・禁止トピック、ナレッジ（固定回答）、商品データの検索も適用されます</strong>。
            会話はテストとして記録され、会話履歴・KPIには含まれません。
          </>
        ) : (
          <>
            ⚠️ プロンプトだけの応答です。<strong>NGワード・禁止トピック、ナレッジ、商品データの検索は適用されません</strong>。
            モデルを指定して比べたいときに使います。
          </>
        )}
      </div>

      <div className='flex flex-wrap items-center gap-3 text-[11px]'>
        <label className='flex cursor-pointer items-center gap-1.5'>
          <input type='radio' checked={fullPipeline} onChange={() => changeMode(true)} />
          本番と同じ処理（NG・ナレッジ・商品検索あり）
        </label>
        <label className='flex cursor-pointer items-center gap-1.5'>
          <input type='radio' checked={!fullPipeline} onChange={() => changeMode(false)} />
          プロンプトのみ（モデル指定可）
        </label>
      </div>

      {/* 履歴表示 */}
      <div className='flex max-h-80 flex-col gap-2 overflow-y-auto rounded-lg bg-surface-secondary p-2.5'>
        {history.length === 0 ? (
          <div className='text-tertiary py-5 text-center text-[11px]'>会話履歴なし</div>
        ) : (
          history.map((m, i) => (
            <div
              key={i}
              className={`rounded-lg p-2 ${m.role === 'user' ? 'bg-surface-emphasis' : 'bg-surface-success'}`}
              style={{ borderLeft: `3px solid var(${m.role === 'user' ? '--color-fill-brand' : '--color-fill-success'})` }}
            >
              <div className='text-tertiary mb-1 text-[9px]'>
                {m.role === 'user' ? 'ユーザー' : 'AI'}
              </div>
              <div className='text-primary whitespace-pre-wrap text-xs'>{m.content}</div>
              {/* 本番と同じ処理で添えた推薦商品（商品検索が効いたかの確認用） */}
              {m.products?.length > 0 && (
                <ul className='text-secondary mt-1.5 list-disc pl-4 text-[11px]'>
                  {m.products.map((pr, j) => (
                    <li key={pr.entity_id || j}>
                      {pr.name || pr.entity_id}
                      {pr.price != null && `（¥${Number(pr.price).toLocaleString()}）`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))
        )}
      </div>

      {/* 入力 */}
      <div className='flex gap-1.5'>
        {!fullPipeline && (
          <select value={model} onChange={e => setModel(e.target.value)} className={`${INPUT} w-auto flex-none`}>
            <option value=''>自動振り分け</option>
            <option value='light'>軽量モデル</option>
            <option value='standard'>標準モデル</option>
          </select>
        )}
        <input
          value={userMessage}
          onChange={e => setUserMessage(e.target.value)}
          // 送信は「送信」ボタンのみ（IMEの変換確定 Enter による誤送信を防ぐ）
          placeholder='質問を入力（送信ボタンで送信）...'
          className={`${INPUT} flex-1`}
        />
        <Button color={ButtonColor.PRIMARY} disabled={loading || !userMessage.trim()} onClick={send}>
          {loading ? '生成中...' : '送信'}
        </Button>
        <Button color={ButtonColor.SECONDARY} onClick={reset}>クリア</Button>
      </div>

      {/* 結果メタ */}
      {result && (
        <div className={`rounded-lg border border-secondary p-2.5 text-[11px] ${result.ok ? 'bg-surface-success' : 'bg-surface-critical'}`}>
          {result.notice && <div className='text-caution mb-1.5'>⚠️ {result.notice}</div>}
          {result.ok ? (
            <div className='text-secondary grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-x-3 gap-y-1'>
              <span><strong className='text-success'>応答の出どころ:</strong> {answerSourceLabel(result.meta)}</span>
              {result.campaign && (
                <span><strong className='text-success'>使用した配信設定:</strong> {result.campaign.name}</span>
              )}
              {Array.isArray(result.recommendations) && result.recommendations.length > 0 && (
                <span><strong className='text-success'>推薦商品:</strong> {result.recommendations.length}件</span>
              )}
              <span><strong className='text-success'>モデル:</strong> {result.meta?.model || '-'}</span>
              {/* NG・ナレッジなど AI を呼ばない応答では値が無いので「-」にする */}
              <span><strong className='text-success'>複雑度:</strong> {result.meta?.complexity_score != null ? `${result.meta.complexity_score} (${result.meta?.complexity_band || '-'})` : '-'}</span>
              <span><strong className='text-success'>レイテンシ:</strong> {result.meta?.latency_ms != null ? `${result.meta.latency_ms}ms` : '-'}</span>
              <span><strong className='text-success'>入力Token:</strong> {result.meta?.input_tokens}</span>
              <span><strong className='text-success'>出力Token:</strong> {result.meta?.output_tokens}</span>
              <span><strong className='text-success'>キャッシュHit:</strong> {result.meta?.prompt_cache_hit ? `読込${result.meta?.cache_read_tokens}t` : '無し'}</span>
              <span><strong className='text-success'>コスト:</strong> ¥{result.meta?.cost_jpy?.toFixed(2) || 0}</span>
            </div>
          ) : (
            <div className='text-critical'>エラー: {result.error}</div>
          )}
        </div>
      )}
    </div>
  )
}


// =====================
// バージョン履歴オーバーレイ
// =====================
function VersionHistoryOverlay({ siteId, profileId, authHeaders, onClose, onRestored }) {
  const [versions, setVersions] = useState([])
  const [loading, setLoading] = useState(true)
  const [restoringId, setRestoringId] = useState(null)

  useEffect(() => {
    fetch(`/api/sites/${siteId}/purchase-assist/ai-profiles/${profileId}/versions`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setVersions(d.items || []))
      .finally(() => setLoading(false))
  }, [siteId, profileId])

  const restore = async (versionId, versionNumber) => {
    if (!window.confirm(`v${versionNumber} の内容で現在のプロファイルを上書きします。\n（新しいバージョンとして履歴に残ります）よろしいですか？`)) return
    setRestoringId(versionId)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ai-profiles/${profileId}/restore/${versionId}`, {
        method: 'POST', headers: authHeaders(),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || `HTTP ${r.status}`)
      }
      onRestored && onRestored()
    } catch (e) {
      alert(`復元失敗: ${e.message}`)
    } finally {
      setRestoringId(null)
    }
  }

  return (
    <ModalOverlay onClose={onClose} className='fixed inset-0 z-modal flex items-center justify-center bg-black/60 p-5'>
      <div onClick={e => e.stopPropagation()} className={`${CARD} max-h-[85vh] w-full max-w-[720px] overflow-auto p-5`}>
        <div className='mb-3 flex items-center justify-between'>
          <h3 className='text-emphasis m-0 text-sm font-bold'>📜 バージョン履歴</h3>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>✕</Button>
        </div>
        {loading ? (
          <div className='text-tertiary py-8 text-center'>読み込み中...</div>
        ) : versions.length === 0 ? (
          <div className='text-tertiary py-8 text-center'>履歴がありません</div>
        ) : (
          <div className='flex flex-col gap-2'>
            {versions.map(v => (
              <div key={v.id} className='rounded-lg border border-secondary bg-surface-secondary p-3'>
                <div className='mb-1.5 flex flex-wrap items-center justify-between gap-2'>
                  <div>
                    <span className='bg-surface-emphasis text-emphasis rounded px-1.5 py-px text-[10px] font-bold'>v{v.version_number}</span>
                    <span className='text-secondary ml-2 text-[11px]'>
                      {v.created_at ? new Date(v.created_at).toLocaleString('ja-JP') : '-'}
                    </span>
                  </div>
                  <Button
                    color={ButtonColor.SECONDARY}
                    disabled={restoringId === v.id}
                    onClick={() => restore(v.id, v.version_number)}
                  >{restoringId === v.id ? '復元中...' : '↺ この版に戻す'}</Button>
                </div>
                {v.change_note && (
                  <div className='text-secondary text-xs'>{v.change_note}</div>
                )}
                <details className='mt-1.5'>
                  <summary className='text-emphasis cursor-pointer text-[10px]'>📋 内容を確認</summary>
                  <pre className='text-secondary mt-1 max-h-60 overflow-auto rounded bg-surface-secondary p-2 text-[10px]'>
                    {JSON.stringify(v.snapshot, null, 2)}
                  </pre>
                </details>
              </div>
            ))}
          </div>
        )}
      </div>
    </ModalOverlay>
  )
}


// =====================
// 共通フィールド
// =====================
function Field({ label, hint, children }) {
  return (
    <div className={FIELD}>
      <label className={`${LABEL} block`}>
        {label}
        {hint && <span className='text-tertiary ml-1.5 text-[10px] font-normal'>— {hint}</span>}
      </label>
      {children}
    </div>
  )
}
